import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, X, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRecipeStudio } from "./RecipeStudioContext";
import BottomActionBar from "./BottomActionBar";
import InspirationHub from "@/components/studio/InspirationHub";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

const placeholderExamples = [
  "Spicy Thai green curry with vegetables",
  "Chocolate chip cookies with sea salt",
  "Creamy carbonara pasta",
  "Chicken tikka masala",
  "Homemade ramen with soft-boiled egg",
  "Fresh Greek salad with feta",
  "Beef tacos with avocado crema",
  "Banana bread with walnuts",
  "Mushroom risotto",
  "Fish and chips",
];

const Step1CoreQuery: React.FC = () => {
  const { coreRecipeQuery, setCoreRecipeQuery } = useRecipeStudio();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);

  const handleExit = () => navigate('/');

  useEffect(() => {
    if (coreRecipeQuery || isFocused) return;
    
    const interval = setInterval(() => {
      setCurrentPlaceholderIndex((prev) => (prev + 1) % placeholderExamples.length);
    }, 2500);
    
    return () => clearInterval(interval);
  }, [coreRecipeQuery, isFocused]);

  const handleNext = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!coreRecipeQuery.trim()) return;
    navigate("../step/2");
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const handleSuggestionClick = (suggestion: string, mode: 'append' | 'replace') => {
    if (mode === 'append') {
      const currentWords = coreRecipeQuery.trim();
      const newQuery = currentWords.endsWith(',') || currentWords.endsWith(' ')
        ? `${currentWords} ${suggestion}`
        : `${currentWords}, ${suggestion}`;
      setCoreRecipeQuery(newQuery);
    } else {
      setCoreRecipeQuery(suggestion);
    }
    inputRef.current?.focus();
  };

  const hasValidInput = coreRecipeQuery.trim().length > 0;

  return (
    <form onSubmit={handleNext} className="w-full max-w-3xl mx-auto px-4">
      {/* Hero section */}
      <div className="text-center pt-8 md:pt-16 pb-8 md:pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
            What would you like to make?
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-xl mx-auto">
            Describe any dish and let our AI chefs craft the perfect recipe
          </p>
        </motion.div>
      </div>

      {/* Input area */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="relative"
      >
        {/* Glow effect */}
        <div
          className={cn(
            "absolute -inset-1 rounded-2xl blur-xl transition-all duration-500",
            (isFocused || hasValidInput)
              ? "bg-gradient-to-r from-orange-400/30 via-pink-400/30 to-purple-400/30 opacity-100"
              : "opacity-0"
          )}
        />

        {/* Main input container */}
        <div
          onClick={handleContainerClick}
          className={cn(
            "relative bg-white rounded-2xl shadow-xl transition-all duration-300 cursor-text overflow-hidden",
            isFocused
              ? "ring-2 ring-orange-400 shadow-2xl"
              : "ring-1 ring-slate-200 hover:ring-slate-300"
          )}
        >
          <div className="flex items-center p-4 md:p-6">
            {/* Sparkle icon */}
            <div className={cn(
              "flex-shrink-0 mr-3 md:mr-4 transition-colors duration-300",
              isFocused || hasValidInput
                ? "text-orange-500"
                : "text-slate-300"
            )}>
              <Sparkles className="w-6 h-6 md:w-7 md:h-7" />
            </div>

            {/* Input */}
            <div className="relative flex-1">
              <input
                ref={inputRef}
                type="text"
                id="core-recipe-query"
                value={coreRecipeQuery}
                onChange={(e) => setCoreRecipeQuery(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                className="w-full text-lg md:text-xl lg:text-2xl bg-transparent border-none outline-none text-slate-900 placeholder-transparent"
                autoComplete="off"
                autoFocus
              />

              {/* Animated placeholder */}
              <AnimatePresence mode="wait">
                {!coreRecipeQuery && (
                  <motion.span
                    key={currentPlaceholderIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0 pointer-events-none text-lg md:text-xl lg:text-2xl text-slate-500 truncate"
                  >
                    {placeholderExamples[currentPlaceholderIndex]}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>

            {/* Clear button */}
            {coreRecipeQuery && (
              <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                type="button"
                aria-label="Clear"
                onClick={(e) => {
                  e.stopPropagation();
                  setCoreRecipeQuery("");
                  inputRef.current?.focus();
                }}
                className="flex-shrink-0 ml-2 p-2 h-10 w-10 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </motion.button>
            )}

            {/* Desktop submit button */}
            <motion.div
              className="hidden md:block ml-4"
              animate={{
                scale: hasValidInput ? 1 : 0.95,
                opacity: hasValidInput ? 1 : 0.5
              }}
            >
              <Button
                type="submit"
                disabled={!hasValidInput}
                className="px-6 py-3 text-base bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
              >
                <span className="mr-2">Continue</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* AI-powered suggestions that appear as you type */}
      <InspirationHub
        currentQuery={coreRecipeQuery}
        onSuggestionClick={handleSuggestionClick}
      />

      {/* Mobile bottom action bar */}
      <BottomActionBar
        onBack={handleExit}
        backLabel="Cancel"
        onNext={() => handleNext()}
        nextLabel="Continue"
        nextDisabled={!hasValidInput}
        hideOnDesktop
      />
    </form>
  );
};

export default Step1CoreQuery;
